import { Analytics } from "@vercel/analytics/next";
import type { AppProps } from "next/app";
import { useEffect, useState } from "react";

import "../styles/globals.css";
import "@fontsource-variable/inter/wght.css";
import "@fontsource/ibm-plex-mono/latin.css";

import { AuthProvider } from "@moment/app/auth/AuthProvider";
import {
	STAGING_IFRAME_BASE_URL,
	getParentFrameOriginBaseUrl,
} from "@moment/app/auth/interop/common";
import { createPlugins } from "@moment/app/components/canvas/easel/prosemirror/plugins";
import { Head } from "@moment/app/components/seo/Head";
import { TopBarProvider } from "@moment/app/components/topbar/TopBarContext";
import { registerCanvasRuntimeFactory } from "@moment/app/runtime/canvas-runtime/registerCanvasRuntimeFactory";
import { registerCellCompiler } from "@moment/app/runtime/cell-compiler/registerCellCompiler";
import { JotaiProvider, registerPluginFactory } from "@moment/state";
import { GoogleTagManager, ThemeProvider, isRunningInIframe } from "@moment/ui";

// Register plugin factory globally
registerPluginFactory(createPlugins);

// Only register runtime + cell compiler inside iframe (or during SSR to satisfy build)
// if (typeof window === "undefined" || isRunningInIframe()) {
registerCanvasRuntimeFactory();
registerCellCompiler();
// }

const STAGING_IFRAME_HOSTNAME = new URL(STAGING_IFRAME_BASE_URL).hostname;
const PROD_IFRAME_HOSTNAME = "iframe.moment.dev";

/**
 * If running inside the iframe hostnames, return the corresponding docs redirect URL.
 */
function getDocsRedirectUrl(): string | null {
	if (typeof window === "undefined") {
		return null;
	}

	const { hostname } = window.location;

	let env: MomentAppEnv;
	if (hostname === STAGING_IFRAME_HOSTNAME) {
		env = "staging";
	} else if (hostname === PROD_IFRAME_HOSTNAME) {
		env = "production";
	} else {
		return null;
	}

	const docsBaseUrl = getParentFrameOriginBaseUrl(env);
	return `${docsBaseUrl}${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export default function App({ Component, pageProps }: AppProps) {
	const [enableGtm, setShowGtm] = useState(true);

	useEffect(() => {
		// Disable GTM inside the iframe
		if (isRunningInIframe()) {
			setShowGtm(false);
			return;
		}

		// If the user tries to load the iframe URL directly, redirect them to the docs site
		const redirectUrl = getDocsRedirectUrl();
		if (redirectUrl) {
			window.location.replace(redirectUrl);
		}
	}, []);

	return (
		<>
			{enableGtm && <GoogleTagManager />}

			<Head
				title={pageProps?.title ?? null}
				description={pageProps?.description ?? null}
				url={pageProps?.url ?? null}
			/>
			<ThemeProvider>
				<AuthProvider>
					<TopBarProvider>
						<JotaiProvider minimal>
							<Component {...pageProps} />
							<Analytics />
						</JotaiProvider>
					</TopBarProvider>
				</AuthProvider>
			</ThemeProvider>
		</>
	);
}
